import { useState, type ReactNode } from "react";

import type { ThreadV4ViewSpec } from "../../api/threadV4Views";
import { Icons, ThreadMark } from "./icons";

export type V4BuiltinViewId = "home" | "customize";
export type V4ActiveViewId = V4BuiltinViewId | string;

interface TopChromeProps {
  projectName: string;
  scopeLabel: string;
  branch: string;
  status: string;
  persistedProject: boolean;
  onAskAgent?: () => void;
}

export function TopChrome({ projectName, scopeLabel, branch, status, persistedProject, onAskAgent }: TopChromeProps) {
  return (
    <div className="v4-chrome">
      <div className="v4-chrome-main">
        <span className="v4-brand">
          <span className="v4-brand-mark"><ThreadMark /></span>
          Thread
          <span className="v4-brand-version">V4</span>
        </span>
        <span className="v4-divider-v" />
        <div className="v4-crumbs">
          <span className={`v4-scope-chip ${persistedProject ? "" : "is-local"}`}>
            {scopeLabel}
          </span>
          <button type="button" title={persistedProject ? "Switch project" : "Local fixture data"}>
            <Icons.Project size={12} />
            <strong>{projectName}</strong>
            {persistedProject ? <Icons.ChevronDown size={11} /> : null}
          </button>
          <span className="v4-crumb-sep">/</span>
          <button type="button" title={persistedProject ? "Switch branch" : "Fixture branch"}>
            <Icons.Branch size={11} />
            <em>{branch}</em>
            {persistedProject ? <Icons.ChevronDown size={11} /> : null}
          </button>
          <span className="v4-status-text">{status}</span>
        </div>
      </div>
      <div className="v4-chrome-middle">
        <button className="v4-btn is-sm is-brand" type="button" onClick={onAskAgent}>
          <Icons.Sparkle size={12} />Ask agent
        </button>
        <button className="v4-btn is-sm" type="button">
          <Icons.History size={12} />History
        </button>
      </div>
      <div className="v4-chrome-actions">
        <button className="v4-iconbtn" type="button" title="Search">
          <Icons.Search size={14} />
        </button>
        <button className="v4-iconbtn" type="button" title="Notifications">
          <Icons.Info size={14} />
        </button>
        <button className="v4-iconbtn" type="button" title="Theme">
          <Icons.Sun size={14} />
        </button>
        <span className="v4-divider-v" />
        <button className="v4-iconbtn" type="button" title="Members">
          <Icons.Users size={14} />
        </button>
        <span className="v4-avatar">AK</span>
      </div>
    </div>
  );
}

interface ViewTabsProps {
  active: V4ActiveViewId;
  customViews: ThreadV4ViewSpec[];
  onChange: (id: V4ActiveViewId) => void;
  onCustomize: () => void;
  onAddView: () => void;
  onSave: () => void;
}

export function ViewTabs({ active, customViews, onChange, onCustomize, onAddView, onSave }: ViewTabsProps) {
  const standard: Array<{ id: V4BuiltinViewId; label: string; icon: ReactNode; kind: string }> = [
    { id: "home", label: "Default", icon: <Icons.Table size={13} />, kind: "live" },
  ];
  return (
    <div className="v4-tabs">
      <div className="v4-tabs-inner" role="tablist" aria-label="Thread V4 views">
        {standard.map((tab) => (
          <button
            key={tab.id}
            className={`v4-tab ${active === tab.id ? "is-active" : ""}`}
            type="button"
            onClick={() => onChange(tab.id)}
          >
            <span className="v4-tab-icon">{tab.icon}</span>
            {tab.label}
            <span className="v4-tab-kind">{tab.kind}</span>
          </button>
        ))}
        {customViews.length ? <span className="v4-tab-divider" /> : null}
        {customViews.map((view) => (
          <button
            key={view.id}
            className={`v4-tab ${active === view.id ? "is-active" : ""}`}
            type="button"
            onClick={() => onChange(view.id)}
          >
            <span className="v4-tab-icon"><Icons.GridIcon size={13} /></span>
            {view.name}
            <span className="v4-tab-kind">{view.category}</span>
          </button>
        ))}
        <button className="v4-tab" type="button" onClick={onAddView} title="New custom view">
          <span className="v4-tab-icon"><Icons.Plus size={13} /></span>
          New view
        </button>
      </div>
      <div className="v4-tabs-end">
        <button className="v4-btn is-sm" type="button" onClick={onCustomize}>
          <Icons.Sliders size={13} />Customize
        </button>
        <button className="v4-btn is-sm" type="button" onClick={onSave}>
          <Icons.Save size={13} />Save as...
        </button>
        <button className="v4-btn is-sm is-brand" type="button">
          <Icons.Share size={13} />Share
        </button>
      </div>
    </div>
  );
}

interface SelectionRailProps {
  branch: string;
  rows: number;
  cols: number;
  assets: number;
  nodes: number;
  edges: number;
  selectedRow?: string | null;
  projectName: string;
  family?: string | null;
  phase?: string | null;
  pivot?: string | null;
}

export function SelectionRail({ branch, rows, cols, assets, nodes, edges, selectedRow, projectName, family, phase, pivot }: SelectionRailProps) {
  return (
    <div className="v4-rail">
      <span className="v4-rail-group">
        <span className="v4-rail-label">scope</span>
        <strong>{projectName}</strong>
        <em>/ {branch}</em>
      </span>
      <span className="v4-divider-v" />
      <span className="v4-rail-group is-shrink">
        <span className="v4-rail-label">selection</span>
        {selectedRow ? (
          <span className="v4-chip is-brand"><Icons.Pin size={10} />{selectedRow}</span>
        ) : (
          <span className="v4-chip">none</span>
        )}
        {family ? <span className="v4-chip">family = {family}</span> : null}
        {phase ? <span className="v4-chip">phase = {phase}</span> : null}
      </span>
      {pivot ? (
        <>
          <span className="v4-divider-v" />
          <span className="v4-rail-group">
            <span className="v4-rail-label">pivot</span>
            <span className="v4-chip is-pivot"><Icons.Sparkle size={10} />{pivot}</span>
          </span>
        </>
      ) : null}
      <span className="v4-rail-spacer" />
      <span className="v4-rail-group v4-rail-metrics">
        <span>{rows} rows</span>
        <span>{cols} cols</span>
        <span>{assets} assets</span>
        <span>{nodes} nodes</span>
        <span>{edges} edges</span>
      </span>
    </div>
  );
}

interface SaveDialogProps {
  defaultName?: string;
  onClose: () => void;
  onSave: (name: string, visibility: "project" | "private" | "public_link", setDefault: boolean) => void;
}

export function SaveDialog({ defaultName = "Tsangpo CFD review", onClose, onSave }: SaveDialogProps) {
  const [name, setName] = useDialogState(defaultName);
  const [visibility, setVisibility] = useDialogState<"project" | "private" | "public_link">("project");
  const [setDefault, setSetDefault] = useDialogState(false);
  return (
    <div className="v4-scrim" role="presentation">
      <div className="v4-dialog" role="dialog" aria-modal="true" aria-labelledby="v4-save-title">
        <div className="v4-dialog-head">
          <div>
            <span className="v4-dialog-eyebrow">Save custom view</span>
            <h2 id="v4-save-title">Persist this dashboard</h2>
          </div>
          <button className="v4-wh-iconbtn" type="button" onClick={onClose} aria-label="Close save dialog">
            <Icons.Close size={14} />
          </button>
        </div>
        <div className="v4-dialog-body">
          <label className="v4-field">
            <span>name</span>
            <input className="v4-input" value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </label>
          <div className="v4-field">
            <span>visibility</span>
            <div className="v4-radio-list">
              <SaveRadio checked={visibility === "project"} icon={<Icons.Folder size={14} />} title="Project · shared with team" description="Everyone on this Thread project sees the view in the tab bar." onChange={() => setVisibility("project")} />
              <SaveRadio checked={visibility === "private"} icon={<Icons.Lock size={14} />} title="Private · only you" description="Only you can open this view. You can share it later." onChange={() => setVisibility("private")} />
              <SaveRadio checked={visibility === "public_link"} icon={<Icons.Globe size={14} />} title="Public link · read-only snapshot" description="A frozen snapshot link; the editable view stays in Thread." onChange={() => setVisibility("public_link")} />
            </div>
          </div>
          <label className="v4-checkbox-row">
            <input type="checkbox" checked={setDefault} onChange={(event) => setSetDefault(event.currentTarget.checked)} />
            Set as project default.
          </label>
        </div>
        <div className="v4-dialog-foot">
          <span>schema: thread.view.v1 · binding · widgets</span>
          <span className="v4-dialog-actions">
            <button className="v4-btn" type="button" onClick={onClose}>Cancel</button>
            <button className="v4-btn is-solid" type="button" onClick={() => onSave(name, visibility, setDefault)}>
              <Icons.Save size={13} />Save view
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

function SaveRadio({ checked, onChange, icon, title, description }: { checked: boolean; onChange: () => void; icon: ReactNode; title: string; description: string }) {
  return (
    <button type="button" className={`v4-radio ${checked ? "is-checked" : ""}`} onClick={onChange}>
      <span className="v4-radio-dot" />
      <span>
        <strong>{icon}{title}</strong>
        <small>{description}</small>
      </span>
    </button>
  );
}

function useDialogState<T>(initial: T): [T, (next: T) => void] {
  return useState(initial);
}
